import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../redux';
import { getFileSignedUrl, isDocORExcelExtension, popup } from '../utils';

const useFileHandler = ({ fileType, file_name, s3_key, extension }) => {
    const dispatch = useDispatch();

    const fetchFileWithProgress = useCallback(
        async (url) => {
            dispatch(setShowProgress(true));

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch file');

            const contentLength = response.headers.get('content-length');
            if (!contentLength) throw new Error('Unable to determine file size');

            const totalSize = parseInt(contentLength, 10);
            let loadedSize = 0;
            const reader = response.body.getReader();
            const chunks = [];

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                chunks.push(value);
                loadedSize += value.length;

                const progress = Math.round((loadedSize / totalSize) * 100);
                dispatch(setLoaderProgress(progress));
            }

            const blob = new Blob(chunks, { type: fileType });
            return URL.createObjectURL(blob);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fileType],
    );

    const viewHandler = useCallback(async () => {
        try {
            dispatch(setLoaderVisibility(true));
            const { data } = await getFileSignedUrl({ filePath: s3_key });

            if (isDocORExcelExtension(extension)) {
                dispatch(
                    setFileViewModalDetails({
                        openFileViewerModal: true,
                        signedURL: data.url,
                        fileName: file_name,
                    }),
                );
            } else {
                const uri = await fetchFileWithProgress(data.url);
                dispatch(
                    setFileViewModalDetails({
                        openFileViewerModal: true,
                        docs: [{ uri, fileType }],
                        fileName: file_name,
                    }),
                );
            }
        } catch (error) {
            popup('error', error.message ?? 'File Maybe Corrupted');
        } finally {
            dispatch(setLoaderVisibility(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file_name, fileType, s3_key, extension]);

    const downloadHandler = useCallback(async () => {
        try {
            dispatch(setLoaderVisibility(true));
            const { data } = await getFileSignedUrl({ filePath: s3_key });
            const uri = await fetchFileWithProgress(data.url);

            const link = document.createElement('a');
            link.href = uri;
            link.setAttribute('download', file_name || '');
            link.click();
        } catch (error) {
            popup('error', error.message ?? 'Unable to download the file');
        } finally {
            dispatch(setLoaderVisibility(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file_name, s3_key]);

    return {
        viewHandler,
        downloadHandler,
    };
};

export default useFileHandler;
